import { Dayjs } from 'dayjs';
import type { Employee as EmployeesEmployee, Member as EmployeesMember } from '../employees/types';

type Employee = EmployeesEmployee & {
  id: string;
  member_id: string;
  patient: string;
  corp: string;
  first_name: string;
  middle_name: string;
  last_name: string;
  name: string;
  email: string;
  dob: string;
  phone_number: string;
  offer_benefits: boolean;
  cobra_id: string | null;
  is_covered: boolean;
  coverage_status_display: string;
  plan_years: PlanYear[];
  offer_benefits_text?: string;
  unlocked_enrollment_end_date: string | null;
  can_manage_cobra: boolean;
  social_security_number: string | null;
};

export type PlanYearOption = PlanYear & {
  plan_year?: string | number;
  coverage_start_date?: string;
  coverage_end_date?: string;
  eligible_for_vision?: boolean;
  enable_cobra?: boolean;
  is_progressive?: boolean;
  coverage_types?: string[];
  available_benefits?: string[];
  can_update_annual_max_period?: boolean;
  employer_networks?: unknown;
  can_update_employer_networks?: boolean;
  employer_networks_choices?: unknown[];
};

export type EmployeePlanYear = PlanYearOption;

export type EmployeeDetailsData = Omit<Employee, 'plan_years'> & {
  plan_years: EmployeePlanYear[];
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  phone?: string;
  has_qualifying_event?: boolean;
  employment_status?: string;
  employment_status_display?: string;
  unlocked_enrollment_end_date?: string;
};

type PlanYear = {
  id: string;
  corporation: string;
  year: number;
  label: string;
  periods: Periods;
  is_current: boolean;
  enrollment: Enrollment;
  employee_unlock_eligibility: EmployeeUnlockEligibility;
  employee_enrollment_is_unlocked: boolean;
  can_update_annual_max_period: boolean;
  annual_max_period_label: string;
  employer_networks: string;
  can_update_employer_networks: boolean;
  employer_networks_choices: EmployerNetworksChoices;
  can_elect_cobra: boolean;
};

type Periods = {
  plan_selection_start_date: string;
  plan_selection_end_date: string;
  enrollment_start_date: string;
  enrollment_end_date: string;
  coverage_start_date: string;
  coverage_end_date: string;
  annual_max_period_start_date: string;
  annual_max_period_end_date: string;
};

type Enrollment = {
  id: string;
  label: string;
  can_change_enrollment_period: boolean;
  enrollment_type: string;
  plan_selection_start_date: string;
  enrollment_start_date: string;
  coverage_start_date: string;
  min_enrollment_start_date: string;
  max_enrollment_start_date: string;
  plan_selection_end_date: string;
  enrollment_end_date: string;
  coverage_end_date: string;
  min_enrollment_end_date: string;
  max_enrollment_end_date: string;
  is_in_plan_selection_period: boolean;
  is_in_enrollment_period: boolean;
  is_in_post_enrollment_pre_coverage_period: boolean;
  is_in_coverage_period: boolean;
  is_re_enrollment: boolean;
  is_past_enrollment: boolean;
  is_plan_selection_in_future: boolean;
  plan_year_display: number;
  broker_fee: string | null;
  modified_at: string;
  proxy_name: string;
  year: number;
  is_initial_plan_year: boolean;
  plan_selection_email_sent_at: string | null;
  plan_selection_7day_reminder_email_sent_at: string | null;
  plan_selection_1day_reminder_email_sent_at: string | null;
  annual_max_period_start_date: string;
  annual_max_period_end_date: string;
  status: string;
  employer_networks: string;
  corporation: string;
  broker: string | null;
  subscription_plan: string;
};

type EmployeeUnlockEligibility = {
  is_eligible: boolean;
  reason: string;
};

type EmployerNetworksChoices = {
  bento_and_cigna: string;
  bento_and_plus_network: string;
  office_fees: string;
};

type EnrollmentDetails = {
  id: string;
  name: string;
  plan_id: number;
  plan_label: string;
  dcs_url: string;
  enrolled_date: string;
  canceled_date: string | null;
  coverage_start_date: string;
  coverage_end_date: string;
  status_display: string;
  family_coverages: Member[];
  relationship: string;
  enrollment_action: string[];
  is_cobra: boolean;
  cobra_id: string | null;
  vision_coverage_status?: string;
  show_vision_coverage_status?: boolean;
};

interface Member extends EmployeesMember {
  id?: string;
  patient?: string;
  member_id?: string;
  coverage_type?: string;
  name: string;
  relationship: string;
  status_display: string;
  coverage_start_date: string;
  coverage_end_date: string;
  enrolled_date: string;
  canceled_date: string;
  dcs_url?: string;
  plan_label?: string;
  plan_id?: number;
  family_coverages?: Member[];
  enrollment_action: string[];
  family_profile_id?: number;
  vision_coverage_status?: string;
}

interface CoverageDataResponse {
  show_dental_and_vision_together_button: boolean;
  dental_and_vision_coverages_match: boolean;
  family_coverages_dental: Coverage[];
  family_coverages_vision: Coverage[];
  primary_coverage_dental: Coverage;
  primary_coverage_vision: Coverage;
  cancel_cobra_disabled: boolean;
  cancel_cobra_disabled_reason: string;
}

interface CoverageMember {
  dentalCoverage: Coverage;
  visionCoverage?: Coverage;
}

interface Coverage {
  coverage_id: number | null;
  coverage_type: string;
  coverage_status: string;
  name: string;
  member_id: string;
  patient_id: number;
  employee_id: number | null;
  family_profile_id: number;
  cobra_id: number | null;
  is_cobra: boolean;
  plan_label: string | null;
  plan_type: string | null;
  plan_id: number | null;
  relationship: string;
  relationship_label: string;
  coverage_start_date: string | null;
  coverage_start_date_error?: string | null;
  coverage_end_date: string | null;
  canceled_date: string | null;
  status_display: string | null;
  dcs_url: string | null;
  coverage_start_date_is_editable: boolean;
  plan_is_editable: boolean;
  min_coverage_start_date: string;
  max_coverage_start_date: string;
  min_coverage_end_date: string;
  max_coverage_end_date: string;
}

interface CobraInfoPlan {
  id: string;
  type: string;
  label: string;
}

interface CobraInfoPlanYear {
  plan_year: string;
  coverage_start_date: string;
  coverage_end_date: string;
  label: string;
  plans: CobraInfoPlan[];
}

interface CobraInfoFamilyMember {
  patient: string;
  name: string;
  relation: string;
  cobra_id?: string;
  has_cobra?: boolean;
  cobra_coverage_start_date: string | null;
  cobra_coverage_end_date: string | null;
  cobra_cancel_status?: string;
  cobra_elect_status?: string;
  is_eligible_for_cobra?: boolean;
  is_scheduled_for_cancelation?: boolean;
}

interface CobraInfo {
  patient?: string;
  employers?: CobraInfoEmployer[];
  family_members: CobraInfoFamilyMember[];
}

interface CobraInfoEmployer {
  corp: string;
  corporation_name: string;
  plan_years: CobraInfoPlanYear[];
}

interface CobraBulkElectMutationProps {
  patient: string | null;
  corp: string;
  plan_year: string;
  plan: string;
  plan_type: string;
  covered_family_patient_ids: string[];
  coverage_start_date: string;
  coverage_period_in_months: number;
}

interface CobraElectFormState {
  employers?: CobraInfoEmployer[];
  employer?: string;
  employerError?: string;
  planYears?: CobraInfoPlanYear[];
  planYear?: string;
  planYearError?: string;
  plans?: CobraInfoPlan[];
  plan?: string;
  planError?: string;
  planType?: string;
  family?: CobraInfoFamilyMember[];
  familyPatientIdsToCover?: string[];
  familyPatientIdsToCoverError?: string;
  effectiveDate?: string;
  effectiveDateError?: string;
  cobraExpirationPeriodMonths?: string;
  cobraExpirationPeriodMonthsError?: string;
}

interface CobraElectFormProps {
  patientId: string | null;
  cobraInfo: CobraInfo | null;
  formState: CobraElectFormState;
  setFormState: (state: CobraElectFormState) => void;
}

interface CobraElectDialogProps {
  open: boolean;
  onClose: () => void;
  patientId?: string | null;
  refresh: () => void;
}

interface CobraElectFormErrors {
  employerError?: string;
  planYearError?: string;
  planError?: string;
  effectiveDateError?: string;
  cobraExpirationPeriodMonthsError?: string;
  familyPatientIdsToCoverError?: string;
}

interface CobraCancelDialogProps {
  open: boolean;
  onClose: () => void;
  patientId?: string | null;
  refresh: () => void;
}

interface CobraCancelFormError {
  cobraFinalDateOfCoverageError?: string;
  cancelCobraIdsError?: string;
}

interface CobraCancelFormState {
  cancelCobraIds: string[];
  finalDateOfCoverage: string | null;
  cancelCobraIdsError: string;
  cobraFinalDateOfCoverageError: string;
}

interface Plan {
  id: string | number;
  label: string;
  type: string;
}

interface EditEnrollmentsDialogProps {
  open: boolean;
  onClose: () => void;
  planYear?: PlanYear;
  coverage: CoverageDataResponse | null;
  showLoader: boolean;
  bulkEnrollEmployee: (payload: EditEnrollmentsPayload) => Promise<void>;
  getPlans: (planYear: PlanYear) => Promise<Plan[]>;
}

export enum EnrollmentStates {
  NotEnrolled = 'not_enrolled_state',
  Enrolled = 'enrolled_state',
  Covered1 = 'covered_state_1',
  Covered2 = 'covered_state_2',
}

type EnrollmentState = `${EnrollmentStates}`;

interface EditEnrollment {
  coverage_id?: number | string | null;
  coverage_type?: string | null;
  employee_id?: number | null;
  family_profile_id?: number | null;
  cobra_id?: number | null;
  is_cobra?: boolean;
  plan_type?: string | null;
  plan_id?: string | number | null;
  plan_state: EnrollmentState;
  coverage_start_date?: string | null;
  coverage_start_date_error?: string | null;
  coverage_end_date?: string | null;
  coverage_end_date_error?: string | null;
  remove_coverage?: boolean;
  undo_cancellation?: boolean;
}

interface EditEnrollmentsPayload {
  primary_coverage_dental?: EditEnrollment;
  family_coverages_dental?: Array<Omit<EditEnrollment, 'employee_id'>>;
  primary_coverage_vision?: EditEnrollment;
  family_coverages_vision?: Array<Omit<EditEnrollment, 'employee_id'>>;
  plan_year?: string;
}

interface CancelEnrollment {
  coverage_id?: number | string | null;
  coverage_type?: string | null;
  coverage_end_date?: string | null;
  coverage_end_date_error?: string | null;
  void_enrollment?: boolean | null;
  undo_cancellation?: boolean | null;
}

interface CancelEnrollmentsPayload {
  dental_coverages?: CancelEnrollment;
  vision_coverages?: CancelEnrollment;
  plan_year?: string;
}

export type {
  Employee,
  PlanYear,
  EnrollmentDetails,
  Member,
  Coverage,
  CoverageMember,
  CoverageDataResponse,
  CobraInfoPlan,
  CobraInfoPlanYear,
  CobraInfoFamilyMember,
  CobraInfo,
  CobraInfoEmployer,
  CobraBulkElectMutationProps,
  CobraElectFormState,
  CobraElectFormProps,
  CobraElectDialogProps,
  CobraElectFormErrors,
  CobraCancelDialogProps,
  CobraCancelFormError,
  CobraCancelFormState,
  Plan,
  EnrollmentState,
  EditEnrollmentsDialogProps,
  EditEnrollment,
  EditEnrollmentsPayload,
  CancelEnrollment,
  CancelEnrollmentsPayload,
};

export interface UpdateCoverageArgs {
  plan: Plan;
  coverageId: string;
  coverageStartDate: Dayjs;
  coverageType: string;
}
